import { useEffect, useRef } from 'react';

import { useCallStore } from '#features/calls/model/callStore';
import { i18n } from '#features/i18n/i18n';
import { toaster } from '#features/ui/components/Toaster';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import {
  getDeviceInfo,
  hasLockScreenPermission,
  openOtherPermissions,
} from '../utils/deviceSettings';

/** Когда в последний раз предлагали настроить экран звонка. */
const ASKED_KEY = 'call-screen-nudge-asked-at';

/**
 * Месяц. Разрешение выдаётся один раз и живёт до обновления приложения; напоминать чаще —
 * значит превратить полезную подсказку в назойливость, из-за которой её закрывают не читая.
 */
const BACKOFF_MS = 30 * 24 * 60 * 60 * 1_000;

const COPY = {
  en: {
    title: 'So calls reach you too',
    description: 'Your phone hides incoming calls until you allow them on the lock screen.',
    action: 'Set up',
  },
  ru: {
    title: 'Чтобы звонки доходили и до вас',
    description: 'Телефон прячет входящие звонки, пока вы не разрешите показ на экране блокировки.',
    action: 'Настроить',
  },
} as const;

const dueToAsk = (now: number): boolean => {
  const raw = localStorage.getItem(ASKED_KEY);
  if (!raw) {
    return true;
  }
  const at = Number.parseInt(raw, 10);
  return !Number.isFinite(at) || at > now || now - at >= BACKOFF_MS;
};

/**
 * Предложить разрешить показ звонка на экране блокировки — в тот момент, когда человек только
 * что звонил сам.
 *
 * Почему именно тут. На MIUI и родственниках входящий звонок не может накрыть экран, пока не
 * выдано разрешение, и отказ молчаливый: звонок звенит в шторке, телефон не просыпается. Раньше
 * об этом спрашивала шторка разрешений при запуске — вместе с батареей и автозапуском, которые
 * на доставку не влияют вовсе, и весь набор переспрашивался после каждого обновления.
 *
 * Человек, который только что позвонил сам, — единственный, кому эта просьба понятна без
 * объяснений: он уже знает, зачем ему звонки. До звонка она была бы разговором ни о чём.
 *
 * Карточка в настройках остаётся и никуда не девается: это способ вернуться к вопросу самому.
 */
export const useCallScreenNudge = () => {
  const phase = useCallStore((state) => state.phase);
  const startedByMe = useCallStore((state) => state.call?.startedByMe ?? false);
  /** Звонок, за который уже показывали подсказку. Иначе она вернётся на каждой перерисовке. */
  const handled = useRef(false);

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }
    if (phase !== 'ended') {
      // Новый звонок — снова разрешаем подсказку, но её всё равно придержит месячный откат.
      handled.current = false;
      return;
    }
    if (!startedByMe || handled.current) {
      return;
    }
    handled.current = true;

    void (async () => {
      if (!dueToAsk(Date.now())) {
        return;
      }
      const info = await getDeviceInfo();
      // Не тот телефон — вопроса не существует: на «чистом» Android звонок накрывает экран сам.
      if (!info || !hasLockScreenPermission(info.manufacturer)) {
        return;
      }
      // `canDrawOverlays` подтверждает настройку, но на MIUI не опровергает её: тумблеры там
      // отдельные и не читаются. Поэтому спрашиваем только когда читаемая половина не выдана.
      if (info.canDrawOverlays === true) {
        return;
      }

      localStorage.setItem(ASKED_KEY, String(Date.now()));
      const copy = COPY[i18n.locale === 'ru' ? 'ru' : 'en'];
      toaster.create({
        type: 'info',
        duration: 12_000,
        title: copy.title,
        description: copy.description,
        action: { label: copy.action, onClick: () => void openOtherPermissions() },
      });
    })();
  }, [phase, startedByMe]);
};
